import { DatadogProvider } from '@datadog/openfeature-browser';
import { Logger, OpenFeature } from '@openfeature/web-sdk';

import { env, getConfigValue } from 'utils/environment';
import logger from 'utils/logger';

import { IDatadogFlagsIdentity, buildDatadogFlagsEvaluationContext } from './evaluation-context';
import { buildDatadogFlagsProvider } from './flag-overrides';

/**
 * Boot waits this long for the first flag configuration before rendering
 * without it. Datadog is a third party on the critical path to first paint,
 * and a request that never answers must not be able to hold the page. On a
 * timeout the provider carries on initialising in the background and flags
 * read their declared fallbacks until it lands.
 */
export const DATADOG_FLAGS_INIT_TIMEOUT_MS = 3000;

const DATA_CENTER_SITES: { [dataCenter: string]: string } = {
  us: 'datadoghq.com',
  eu: 'datadoghq.eu',
};

/**
 * The configured data centre is the same short form RUM is initialised with
 * (`'us'`, `'eu'`, or empty). The flagging SDK wants the intake host instead.
 */
export const datadogFlagsSite = (dataCenter?: string): string =>
  DATA_CENTER_SITES[(dataCenter || 'us').toLowerCase()] ?? DATA_CENTER_SITES.us;

/**
 * The SDK reports recoverable conditions — a flag it has no configuration
 * for, a context change mid-fetch — through `error`. Left alone they arrive
 * in monitoring as application errors and bury the ones that are.
 */
const openFeatureLogger: Logger = {
  debug: () => {},
  info: () => {},
  warn: message => logger.warn(String(message)),
  error: message => logger.warn(String(message)),
};

/**
 * Stops waiting after `ms` while leaving the work running.
 *
 * The rejection handler is attached to the work itself rather than left to
 * the caller's `catch`. Once the timer has won the race nothing is awaiting
 * the work any more, so a fetch that fails *after* the timeout would be an
 * unhandled rejection and the never-fatal logging would not run — the exact
 * failure this function exists to contain, escaping through it.
 */
const withTimeout = (work: Promise<unknown>, ms: number): Promise<unknown> => {
  const settled = work.catch(error => {
    logger.error({ error, message: 'Error initialising Datadog flags' });
  });

  return Promise.race([
    settled,
    new Promise(resolve => {
      setTimeout(resolve, ms);
    }),
  ]);
};

/**
 * Stands the Datadog provider up for the whole app.
 *
 * Credentials come from the configuration this repo already has: the same
 * application id and client token RUM is initialised with, which is what
 * scopes a flag to the right application, plus the environment. Nothing here
 * needs a new environment variable.
 *
 * The evaluation context is handed to `setProviderAndWait` rather than set
 * afterwards, so the configuration is fetched for this guest and the first
 * evaluation any component makes is already the contextualised one instead of
 * a fallback that a later render corrects.
 */
export const initDatadogFlags = async (identity: IDatadogFlagsIdentity = {}): Promise<void> => {
  try {
    const { rum } = getConfigValue('datadog');

    OpenFeature.setLogger(openFeatureLogger);

    const datadogProvider = new DatadogProvider({
      applicationId: rum.applicationId,
      clientToken: rum.clientToken,
      site: datadogFlagsSite(rum.dataCenter),
      env: env(),
      // Puts the flag a session was served onto that session's RUM events, so
      // behaviour can be split by variant. It adds content to events RUM
      // already bills for.
      enableRumFeatureFlagTracking: true,
    });

    await withTimeout(
      OpenFeature.setProviderAndWait(
        buildDatadogFlagsProvider(datadogProvider),
        buildDatadogFlagsEvaluationContext(identity)
      ),
      DATADOG_FLAGS_INIT_TIMEOUT_MS
    );
  } catch (error) {
    // Never fatal. Every flag has a declared fallback, so the app renders the
    // pre-rollout experience rather than not rendering.
    logger.error({ error, message: 'Error initialising Datadog flags' });
  }
};

/**
 * Re-points evaluation at a different guest — after a sign-in or sign-out —
 * without rebuilding the provider. The SDK re-fetches the configuration for
 * the new context and re-renders anything reading a flag whose value moved.
 */
export const setDatadogFlagsIdentity = async (
  identity: IDatadogFlagsIdentity = {}
): Promise<void> => {
  try {
    await OpenFeature.setContext(buildDatadogFlagsEvaluationContext(identity));
  } catch (error) {
    logger.error({ error, message: 'Error updating the Datadog flags evaluation context' });
  }
};
